import { keyframes } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

const pulse = keyframes`
  0% {
    border-color: rgba(249, 0, 211, 1);
  }
  50% {
    border-color: rgba(249, 0, 211, 0.3);
  }
  100% {
    border-color: rgba(249, 0, 211, 1);
  }
`;

export interface UseAutoScrollHighlightProps {
  autoScroll?: boolean;
  onScrolled?: () => void;
  duration?: number;
}

/**
 * A custom hook that provides functionality for auto-scrolling and highlighting
 * an element. It scrolls the referenced element into view and applies a highlight
 * effect for a specified duration.
 *
 * @param {boolean} [props.autoScroll=false] - Determines whether the element should auto-scroll into view.
 * @param {() => void} [props.onScrolled] - Callback function invoked after the highlight effect ends.
 * @param {number} [props.duration=10000] - Duration (in milliseconds) for which the highlight effect is applied.
 *
 * @returns {Object} - An object containing the ref for the element and the styles for the highlight effect.
 * @returns {React.RefObject<HTMLDivElement>} ref - A React ref to be attached to the target element.
 * @returns {React.CSSProperties} highlightSx - The styles to apply for the highlight effect.
 */
export function useAutoScrollHighlight({
  autoScroll = false,
  onScrolled,
  duration = 5000,
}: UseAutoScrollHighlightProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (autoScroll && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
      setHighlight(true);

      const timeout = setTimeout(() => {
        setHighlight(false);
        onScrolled?.();
      }, duration);

      return () => clearTimeout(timeout);
    }
  }, [autoScroll, onScrolled, duration]);

  const highlightSx = highlight
    ? {
        border: '2px solid rgba(249, 0, 211, 1)',
        borderRadius: 1,
        animation: `${pulse} 1s ease-in-out`,
        animationIterationCount: 5,
      }
    : {};

  return { highlight, ref, highlightSx };
}
